import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Grid, Paper, TextField, InputAdornment, IconButton,
    ToggleButton, ToggleButtonGroup, Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ReportCard from './ReportCard';
import { iconeDoRelatorio } from '../config/iconesRelatorio';
import { CATEGORIAS, filtrarRelatorios, contarPorCategoria } from '../config/relatorios';
import { isAdmin } from '../services/auth';

const CARD_SIZE = { xs: 12, sm: 6, lg: 4 };

/**
 * Catálogo de relatórios: busca por nome ou descrição, filtro por categoria e
 * cards compactos que levam à página do relatório.
 *
 * O catálogo só escolhe. Filtros, tabela e exportação ficam na página de cada
 * relatório (RelatorioViewer) — antes cada card trazia o próprio formulário e os
 * próprios botões de PDF/CSV, o que fazia a tela crescer sem limite conforme o
 * módulo foi de 3 para 40 relatórios.
 */
export default function Relatorios() {
    const navigate = useNavigate();
    const [busca, setBusca] = useState('');
    const [categoria, setCategoria] = useState('Todos');

    const termo = busca.trim();

    // O relatório de usuários é ADMIN no back-end; esconder o card evita o card
    // que abre em 403. O perfil vem do token, então não muda durante a sessão.
    const admin = useMemo(() => isAdmin(), []);

    const encontrados = useMemo(() => filtrarRelatorios(termo, categoria, admin), [termo, categoria, admin]);
    const contagem = useMemo(() => contarPorCategoria(termo, admin), [termo, admin]);

    const limpar = () => { setBusca(''); setCategoria('Todos'); };

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5, color: 'secondary.main' }}>
                Relatórios
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Escolha um relatório para definir os filtros e consultar os resultados.
            </Typography>

            <Paper variant="outlined" sx={{ p: 1.5, mb: 2.5 }}>
                <Box
                    sx={{
                        display: 'flex', flexWrap: 'wrap', gap: 1.5,
                        alignItems: 'center', justifyContent: 'space-between',
                    }}
                >
                    <TextField
                        size="small"
                        placeholder="Buscar por nome ou descrição"
                        value={busca}
                        onChange={(e) => setBusca(e.target.value)}
                        sx={{ flexGrow: 1, minWidth: 240, maxWidth: 420 }}
                        inputProps={{ 'aria-label': 'Buscar relatório por nome ou descrição' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: busca ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setBusca('')} aria-label="Limpar busca">
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />

                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={categoria}
                        onChange={(_e, v) => { if (v) setCategoria(v); }}
                        aria-label="Filtrar relatórios por categoria"
                        sx={{ flexWrap: 'wrap' }}
                    >
                        {CATEGORIAS.map(c => (
                            <ToggleButton key={c} value={c} sx={{ px: 1.5, gap: 0.75 }}>
                                {c}
                                <Chip
                                    label={contagem[c] ?? 0}
                                    size="small"
                                    sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
                                />
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>
            </Paper>

            {encontrados.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 5, textAlign: 'center' }} role="status" aria-live="polite">
                    <SearchOffIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Nenhum relatório encontrado
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {busca.trim()
                            ? `Não há relatório com “${busca.trim()}”`
                            : 'Não há relatório'}
                        {categoria !== 'Todos' && ` na categoria ${categoria}`}.{' '}
                        <Box
                            component="button"
                            type="button"
                            onClick={limpar}
                            sx={{
                                background: 'none', border: 0, p: 0, font: 'inherit',
                                color: 'primary.main', cursor: 'pointer', textDecoration: 'underline',
                            }}
                        >
                            Limpar os filtros
                        </Box>
                    </Typography>
                </Paper>
            ) : (
                <Grid container spacing={2} alignItems="stretch">
                    {encontrados.map(r => (
                        <Grid size={CARD_SIZE} key={r.key} sx={{ display: 'flex' }}>
                            <ReportCard
                                icon={iconeDoRelatorio(r.key)}
                                titulo={r.titulo}
                                descricao={r.descricao}
                                categoria={r.categoria}
                                onAbrir={() => navigate(`/relatorios/${r.key}`)}
                            />
                        </Grid>
                    ))}

                    {/* Só na aba Todos e sem busca: não é um dos relatórios
                        do catálogo, é o atalho para montar um. */}
                    {categoria === 'Todos' && !termo && (
                        <Grid size={CARD_SIZE} sx={{ display: 'flex' }}>
                            <ReportCard
                                icon={<AutoAwesomeIcon sx={{ fontSize: 26, color: 'primary.main' }} />}
                                titulo="Personalizável"
                                descricao="Escolha um relatório como base e as colunas que você quer ver."
                                categoria="Sob medida"
                                onAbrir={() => navigate('/relatorios/personalizado')}
                            />
                        </Grid>
                    )}
                </Grid>
            )}
        </Box>
    );
}
